import { For, Show, createMemo } from "solid-js";
import { useI18n } from "../i18n/provider.tsx";
import { allQuestionIds, topicQuestionIds } from "../lib/tree.ts";
import {
  TreeFrame,
  TriCheckbox,
  Twisty,
  fillOf,
  showsBranch,
  treeStyles as styles,
  useFolding,
} from "./tree-shell.tsx";
import type { TreeQuestion, TreeTopic } from "../api/domain.ts";

/**
 * All this picker needs of a question. Narrower than `TreeQuestion` on purpose:
 * callers assemble the loose list themselves, and a wider type would force them
 * to invent an answer coverage this component never shows.
 */
export type PickableQuestion = Pick<TreeQuestion, "id" | "title" | "informational">;

export type TreeSelectProps = {
  /** The topics to pick from, as the tree endpoint returns them. */
  topics: TreeTopic[];
  /**
   * The ticked questions. Topics are never selected on their own: a topic reads
   * as ticked exactly when every question of it is.
   */
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  /** The row that ticks every question at once. */
  allLabel: string;
  /**
   * Questions no topic claims, listed under their own heading. Without this
   * they would be unreachable: the tree is built from topic membership, so a
   * question outside it appears nowhere and can never be assigned.
   */
  loose?: readonly PickableQuestion[];
  /** Names the group the loose questions are shown in. */
  looseLabel?: string;
};

/**
 * One selector over the whole catalog: everything, one topic, or single
 * questions, all through the same checkbox. Which of those a tick was is not a
 * distinction the selector keeps. It reports the questions, and the caller
 * decides how to store them.
 */
export const TreeSelect = (props: TreeSelectProps) => {
  const i18n = useI18n();
  const folding = useFolding(
    () => props.topics,
    (topic) => topic.questions.length > 0,
  );

  const selected = createMemo(() => new Set(props.selected));
  const loose = createMemo(() => props.loose ?? []);
  const everything = createMemo(() => [
    ...allQuestionIds(props.topics),
    ...loose().map((question) => question.id),
  ]);

  const emit = (next: ReadonlySet<string>) =>
    // Catalog order, so what the caller stores does not depend on click order.
    props.onChange(everything().filter((id) => next.has(id)));

  const setMany = (questions: readonly string[], on: boolean) => {
    const next = new Set(selected());
    for (const id of questions) {
      if (on) next.add(id);
      else next.delete(id);
    }
    emit(next);
  };

  const Leaf = (own: { question: PickableQuestion }) => (
    <li class={styles.leaf}>
      <div class={styles.row}>
        <span class={styles.twistyGap} aria-hidden="true" />
        <label class={styles.pick}>
          <input
            type="checkbox"
            checked={selected().has(own.question.id)}
            onChange={(event) => setMany([own.question.id], event.currentTarget.checked)}
          />
          <span class={styles.questionTitle}>{own.question.title}</span>
        </label>
        <Show when={own.question.informational}>
          <span class={styles.tag}>{i18n.t().catalog.informational}</span>
        </Show>
      </div>
    </li>
  );

  const Topic = (branch: { topic: TreeTopic }) => {
    const questions = createMemo(() => topicQuestionIds(branch.topic));
    const fill = createMemo(() => fillOf(questions(), selected()));

    return (
      <li class={styles.node}>
        <div class={styles.row}>
          <Twisty topic={branch.topic} folding={folding} />
          <label class={styles.pick}>
            <TriCheckbox
              fill={fill()}
              disabled={questions().length === 0}
              onToggle={(on) => setMany(questions(), on)}
            />
            <span class={styles.topicTitle}>{branch.topic.title}</span>
          </label>
          <Show when={questions().length > 0}>
            <span class={styles.count}>
              {questions().filter((id) => selected().has(id)).length} {i18n.t().common.of}{" "}
              {questions().length}
            </span>
          </Show>
        </div>

        <Show when={showsBranch(branch.topic, folding)}>
          <ul class={styles.branch}>
            <For each={branch.topic.questions}>{(question) => <Leaf question={question} />}</For>
          </ul>
        </Show>
      </li>
    );
  };

  const total = () => everything().length;
  const picked = () => everything().filter((id) => selected().has(id)).length;

  return (
    <TreeFrame
      folding={folding}
      filled={total() > 0}
      summary={`${picked()} ${i18n.t().common.of} ${total()} ${i18n.t().tree.selected}`}
      lead={
        <label class={styles.pick}>
          <TriCheckbox
            fill={fillOf(everything(), selected())}
            disabled={total() === 0}
            onToggle={(on) => setMany(everything(), on)}
          />
          <span class={styles.allLabel}>{props.allLabel}</span>
        </label>
      }
    >
      <For each={props.topics}>{(topic) => <Topic topic={topic} />}</For>
      <Show when={loose().length > 0}>
        <li class={styles.node}>
          <div class={styles.row}>
            <span class={styles.twistyGap} aria-hidden="true" />
            <label class={styles.pick}>
              <TriCheckbox
                fill={fillOf(
                  loose().map((question) => question.id),
                  selected(),
                )}
                onToggle={(on) =>
                  setMany(
                    loose().map((question) => question.id),
                    on,
                  )
                }
              />
              <span class={styles.topicTitle}>
                {props.looseLabel ?? i18n.t().tree.unassigned}
              </span>
            </label>
          </div>
          <ul class={styles.branch}>
            <For each={loose()}>{(question) => <Leaf question={question} />}</For>
          </ul>
        </li>
      </Show>
    </TreeFrame>
  );
};
